/**
 * Step 1 of module #3: rebuild a tree from PCRE2's callout enumeration.
 *
 * `pcre2_callout_enumerate` hands back an ordered list of the items in a
 * compiled pattern, each with its span in the SOURCE. Nesting is implied by
 * the order rather than stated, so a stack walk recovers it. This file is the
 * whole of that claim; PLAN.md, "Module #3", step 1, and `ideas/regexray.md`
 * for why the architecture rests on it.
 *
 * Three things the enumeration does that the design did not anticipate, each
 * found by a pattern rather than by reading the documentation. They are
 * commented where they are handled:
 *
 *   1. an item ABSORBS following source that compiles to nothing, so `|`, `(`
 *      and `)` items can carry a tail;
 *   2. source that compiles to nothing and has no item in front of it is
 *      simply UNCOVERED, anywhere in the pattern;
 *   3. a bounded repeat REPLICATES its group's items, so positions run
 *      backwards.
 */

/** The end marker, and nothing else, has zero length at the end. */
const isEndMarker = (item, pattern) => item.len === 0 && item.pos === pattern.length;

/**
 * Does a span that opens with `(` open a GROUP, and if so what KIND?
 * Returns null for the forms that open with `(` and are complete atoms.
 *
 * **Read only the PREFIX, never the suffix.** The tempting test is that an
 * atom ends with `)` and a group open does not, and it is wrong twice over:
 * `(?(1)` ends with `)` and opens a conditional, and - the one that is
 * invisible until it happens - an item absorbs whatever follows it that has
 * no item of its own, so `((?#c)a)` arrives with the group open as `((?#c)`
 * and `(?x)  ` arrives with two trailing spaces. Both end with characters
 * that say nothing about what they opened. The opening syntax is a bounded
 * prefix, so classify on that and the absorbed tail cannot lie.
 *
 * The kind is not extra: `ideas/regexray.md` claims the item text names the
 * group kind with no grammar knowledge required, and this is that claim
 * written out. The diagram needs it.
 */
export function classifyOpen(text) {
  if (!text.startsWith("(")) return null;
  if (text.startsWith("(*")) return null;               // (*FAIL) (*SKIP:n) - a verb
  const body = text.slice(1);
  if (!body.startsWith("?")) return "capture";          // ( - and anything absorbed after it

  const after = body.slice(1);
  if (after === "") return "conditional";               // (? - the condition is an assertion, which follows as its own item
  if (after.startsWith("(")) return "conditional";      // (?(1) (?(R) (?(R&n) (?(DEFINE)
  if (after.startsWith(":")) return "non-capture";
  if (after.startsWith(">")) return "atomic";
  if (after.startsWith("|")) return "branch-reset";
  if (after.startsWith("=")) return "lookahead";
  if (after.startsWith("!")) return "negative-lookahead";
  if (after.startsWith("<=")) return "lookbehind";
  if (after.startsWith("<!")) return "negative-lookbehind";
  if (after.startsWith("<") || after.startsWith("'")) return "named";   // (?<n> (?'n'
  if (after.startsWith("P<")) return "named";                            // (?P<n>
  if (/^[P&R C]/.test(after)) return null;              // (?P>n) (?P=n) (?&n) (?R) (?C1)
  if (/^[+-]?\d/.test(after)) return null;              // (?1) (?-1) (?+1)

  // What is left is an option setting, which is an atom on its own - (?i),
  // (?-i) - and a group when it scopes one: (?i:...).
  const options = /^[imnsxJUaltu^-]*/.exec(after)[0];
  return after.slice(options.length).startsWith(":") ? "scoped-options" : null;
}

const group = (open, groupKind) => ({
  kind: groupKind === "root" ? "root" : "group",
  groupKind,
  open,
  close: "",
  branches: [{ separator: "", children: [] }],
});

/**
 * @param {string} pattern the ORIGINAL pattern text the offsets index into
 * @param {{pos:number,len:number}[]} items in enumeration order
 * @returns {object} the root node
 */
export function buildTree(pattern, items) {
  const root = group("", "root");
  const stack = [root];
  let covered = 0;

  const branchOf = (node) => node.branches[node.branches.length - 1];

  for (const item of items) {
    // **A BOUNDED REPEAT ON A GROUP REPLICATES THE GROUP'S ITEMS**, because
    // PCRE2 replicates the compiled bytecode and the enumeration walks the
    // COMPILED pattern rather than the source. `(?:ab){2}` enumerates the open
    // once and then `a`, `b`, `){2}` twice; `(?:a){2,4}` four times; nesting
    // multiplies. The copies point back at the SAME source offsets, so the one
    // reliable tell is that the position runs backwards - positions are
    // otherwise monotonic, gaps included. Skip anything behind the watermark
    // and the tree is built from the source once, which is what a diagram of
    // the source wants.
    //
    // Found by a stock IPv4 pattern, which pushed one group and popped three.
    if (item.pos < covered) continue;

    // **SOURCE THAT COMPILES TO NOTHING AND HAS NOTHING IN FRONT OF IT GETS NO
    // ITEM AT ALL**, so it is uncovered. `(?#comment)`, `\Q`, an explicit
    // `(?C1)` callout and an option setting like `(?i)` all do this. Usually
    // the item in front absorbs them - which is why this looked at first like
    // a preamble that only happened at position 0 - but there is nothing in
    // front of `(?i)` in `(?<n>(?i)|x)`, and it vanishes mid-pattern.
    //
    // Carry it as an inert leaf rather than dropping it: it is source the
    // reader typed, the diagram will want to show or dim it, and keeping it is
    // what lets the tree be checked by writing it back out.
    if (item.pos > covered) {
      branchOf(stack[stack.length - 1]).children.push({ kind: "inert", text: pattern.slice(covered, item.pos) });
      covered = item.pos;
    }

    if (isEndMarker(item, pattern)) continue;
    covered = item.pos + item.len;

    const text = pattern.slice(item.pos, item.pos + item.len);
    const top = stack[stack.length - 1];
    const head = text[0];

    if (head === "|") {
      // The separator is stored rather than assumed, because it absorbs too:
      // `a|\Qq\Eb` enumerates the alternation as `|\Q`.
      top.branches.push({ separator: text, children: [] });
    } else if (head === ")") {
      if (stack.length === 1) throw new Error(`unbalanced ) at ${item.pos}`);
      stack.pop().close = text;                   // ")" plus any quantifier, plus anything absorbed: ")*", ")++", ")\\Q"
    } else if (head === "(" && classifyOpen(text) !== null) {
      const node = group(text, classifyOpen(text));
      // Where this group OPENS in the pattern. The step debugger points at
      // items by span, and a group's own span is its opening syntax.
      node.span = { start: item.pos, end: item.pos + item.len };
      branchOf(top).children.push(node);
      stack.push(node);
    } else {
      branchOf(top).children.push({
        kind: "atom",
        text,                                        // carries its own quantifier
        span: { start: item.pos, end: item.pos + item.len },
      });
    }
  }

  if (stack.length !== 1) throw new Error(`${stack.length - 1} group(s) left open`);
  if (covered < pattern.length) {
    branchOf(root).children.push({ kind: "inert", text: pattern.slice(covered) });
  }

  return root;
}

/**
 * Write the tree back out as pattern text. **This is the acceptance test**: a
 * tree that reproduces its own pattern exactly has its nesting, its branch
 * splits and its quantifier association all right, and no case can pass by
 * being drawn plausibly.
 */
export function toSource(node) {
  const body = node.branches
    .map((b) => b.separator + b.children.map((c) => (c.kind === "group" ? toSource(c) : c.text)).join(""))
    .join("");
  return node.open + body + node.close;
}

/** A shape a diagram can be drawn from, and a readable failure when it is wrong. */
export function describe(node, depth = 0) {
  const pad = "  ".repeat(depth);
  const lines = [];
  node.branches.forEach((branch, i) => {
    if (node.branches.length > 1) lines.push(`${pad}branch ${i + 1}`);
    for (const child of branch.children) {
      if (child.kind === "group") {
        const quantifier = child.close.length > 1 ? ` quantified ${JSON.stringify(child.close.slice(1))}` : "";
        lines.push(`${pad}  ${child.groupKind} ${JSON.stringify(child.open)}${quantifier}`);
        lines.push(...describe(child, depth + 2));
      } else if (child.kind === "inert") {
        lines.push(`${pad}  (inert) ${JSON.stringify(child.text)}`);
      } else {
        lines.push(`${pad}  ${JSON.stringify(child.text)}`);
      }
    }
  });
  return lines;
}

/**
 * What each capture group is called, keyed by its number: `{ 1: "local",
 * 2: "domain" }`. A group without a name is absent, so a caller falls back
 * to "Group n" for it.
 *
 * **Numbering is by opening parenthesis in source order, counting only the
 * groups that capture**, which is PCRE2's rule and is what makes this
 * derivable from the tree without asking the engine for its name table.
 * The one construct that breaks the rule is branch reset, `(?|`, where the
 * alternatives share numbers; a pattern containing one gets no names at all
 * rather than wrong ones, since a wrong name on a column is worse than a
 * number.
 */
export function captureNames(root) {
  const names = {};
  let number = 0;
  let unsafe = false;

  const walk = (node) => {
    if (node.kind !== "group" && node.kind !== "root") {
      return;
    }

    if (node.groupKind === "branch-reset") {
      unsafe = true;
    }

    if (node.groupKind === "capture" || node.groupKind === "named") {
      number += 1;
      const named = /^\(\?P?[<']([A-Za-z_][A-Za-z0-9_]*)[>']$/.exec(node.open);

      if (named) {
        names[number] = named[1];
      }
    }

    for (const branch of node.branches) {
      for (const child of branch.children) {
        walk(child);
      }
    }
  };

  walk(root);

  return unsafe ? {} : names;
}
